import moment from 'moment'
import { getPlaceData } from '@pcln/home-components'
import { PKG_DATE_FORMAT, PKG_DATE_TIME_FORMAT } from '../constants/Constants'
import checkIsCustomCarSearchType from './checkIsCustomCarSearchType'
import { formatDate } from './date-helpers'

export const getComponentsFromTripType = (tripType, option) => {
  let index = 0

  const addProductToComponents = (productItem, components, shouldUnshift) => {
    const updatedComponents = [...components]

    shouldUnshift
      ? updatedComponents.unshift(productItem)
      : updatedComponents.push(productItem)

    index++
    return updatedComponents
  }

  const addMultipleHotelsToComponents = (components, option) => {
    const mappedHotels = option.hotels.map(hotel => {
      const mappedHotel = {
        productId: 5,
        index,
        type: 'STAY',
        query: {
          totalNumRooms: option.roomCount,
          destination: getPlaceData(hotel.locationObject, true),
          travelStartDate: {
            value: hotel.startDate
              ? moment(hotel.startDate).format(PKG_DATE_FORMAT)
              : ''
          },
          travelEndDate: {
            value: hotel.endDate
              ? moment(hotel.endDate).format(PKG_DATE_FORMAT)
              : ''
          }
        }
      }
      index++
      return mappedHotel
    })

    return mappedHotels.concat([...components])
  }

  const addHotelToComponents = (components, option) => {
    const productItem = {
      productId: 5,
      index,
      type: 'STAY',
      query: Object.assign(
        {
          totalNumRooms: option.roomCount
        },
        option.isPartialStayDateValid
          ? {
            travelStartDate: {
              value: option.hotelStartDate
            },
            travelEndDate: {
              value: option.hotelEndDate
            }
          }
          : {}
      )
    }

    return addProductToComponents(productItem, components, true)
  }

  const getFlightQueryDates = ({ startDate, endDate, flightItineraryTypeCode }) => {
    const queryDates = {}

    if (startDate) {
      queryDates.travelStartDate = moment(startDate).format(PKG_DATE_FORMAT)
    }

    if (flightItineraryTypeCode === 'OW') {
      queryDates.travelEndDate = ''
    } else if (endDate) {
      queryDates.travelEndDate = moment(endDate).format(PKG_DATE_FORMAT)
    }

    return queryDates
  }

  const addFlightToComponents = (components, option) => {
    const productItem = {
      productId: 1,
      index,
      type: 'FLY',
      query: {
        itineraryTypeCode: option.flightItineraryTypeCode || 'RT',
        ...getFlightQueryDates(option)
      }
    }

    return addProductToComponents(productItem, components)
  }

  const getRCPlaceData = (carLocation) => {
    if (!!carLocation && typeof carLocation === 'object') {
      return {
        ...getPlaceData(carLocation),
        cityName: carLocation.cityName,
        lat: carLocation.lat,
        lon: carLocation.lon,
        stateCode: carLocation.stateCode,
        postalCode: carLocation.postalCode,
        addressLine1: carLocation.addressLine1
      }
    }
    return carLocation
  }

  const buildCarQuery = ({
    hasFlights,
    hasHotels,
    carSearchType,
    flightItineraryTypeCode,
    hotels,
    isIncludeAirportLocations,
    carPickUpTime = '0:0',
    carPickUpDate,
    carDropOffTime = '0:0',
    carDropOffDate,
    carPickUpLocationObject = {},
    arrivalLocation = {}
  }) => {
    const isOneWay = flightItineraryTypeCode === 'OW'

    if (checkIsCustomCarSearchType({
      isOneWay,
      flightItineraryTypeCode,
      carSearchType,
      hasFlights,
      hasHotels,
      hotels
    })) {
      const carPickUpTimeSplit = carPickUpTime.split(':')
      const carDropOffTimeSplit = carDropOffTime.split(':')

      const {
        addressLine1,
        cityName,
        content,
        countryName,
        id,
        itemName,
        countryCode,
        lat,
        lon,
        name,
        stateCode,
        type,
        postalCode
      } = carPickUpLocationObject

      const locationName = content || name || arrivalLocation.content || itemName

      const isCityOrAirport = type === 'CITY' || type === 'AIRPORT'
      const returnLocationProps = !isCityOrAirport ? {
        cityName,
        countryName,
        isoCountryCode: countryCode,
        lat,
        lon,
        stateCode,
        postalCode,
        addressLine1
      } : {}

      return {
        query: {
          isIncludeAirportLocations,
          pickupDateTime: moment(carPickUpDate).add(carPickUpTimeSplit[0], 'hours').add(carPickUpTimeSplit[1], 'minutes').format(PKG_DATE_TIME_FORMAT),
          returnDateTime: moment(carDropOffDate).add(carDropOffTimeSplit[0], 'hours').add(carDropOffTimeSplit[1], 'minutes').format(PKG_DATE_TIME_FORMAT),
          returnLocation: {
            id: id || arrivalLocation.id,
            name: locationName,
            type: type || arrivalLocation.type,
            ...returnLocationProps
          }
        }
      }
    } else if (hasFlights && isOneWay && hasHotels) {
      return {
        query: {
          pickupDateTime: moment(hotels[0].startDate)
            .add(10, 'hours')
            .format(PKG_DATE_TIME_FORMAT),
          returnDateTime: moment(hotels[0].endDate)
            .add(10, 'hours')
            .format(PKG_DATE_TIME_FORMAT),
          returnLocation: getPlaceData(hotels[0].locationObject)
        }
      }
    } else if (option.rcPickupDate || option.rcDropoffDate || option.rcLocationObject) {
      return {
        query: {
          pickupDateTime: option.rcPickupDate
            ? `${formatDate(option.rcPickupDate, 'YYYY-MM-DD')}T${
              option.rcPickupTime
            }`
            : '',
          returnDateTime: option.rcDropoffDate
            ? `${formatDate(option.rcDropoffDate, 'YYYY-MM-DD')}T${
              option.rcDropoffTime
            }`
            : '',
          pickupLocation: getRCPlaceData(option.rcLocationObject),
          returnLocation: getRCPlaceData(option.rcLocationObject),
          isIncludeAirportLocations: option.pickupCarIncludeAirport
        }
      }
    }
    return {}
  }

  const addCarToComponents = (components, option) => {
    const productItem = Object.assign(
      {
        productId: 8,
        type: 'DRIVE',
        index
      },
      buildCarQuery(option),
      option.isCarRequired
        ? {}
        : { isRequired: false }
    )

    return addProductToComponents(productItem, components)
  }

  const mapComponent = (components, type, option) => {
    switch (type) {
      case 'STAY':
        return option.hotels
          ? addMultipleHotelsToComponents(components, option)
          : addHotelToComponents(components, option)
      case 'FLY':
        return addFlightToComponents(components, option)
      case 'DRIVE':
        return addCarToComponents(components, option)
      default:
        return components
    }
  }

  return tripType
    .split(':')
    .reduce(
      (components, type) => mapComponent(components, type, option),
      []
    )
}
